import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchBrandConfiguration } from '../services/brand-config.service';
import { MarcaConfig } from '../utils/types';

// Rutas especiales que no son marcas (no intentar cargar configuración)
const SPECIAL_ROUTES = ['maintenance', '404', 'skeletons'];

interface BrandConfigContextValue {
  config: MarcaConfig | null;
  loading: boolean;
  error: string | null;
  brandKeyword: string;
  isNotFound: boolean;
}

const BrandConfigContext = createContext<BrandConfigContextValue | undefined>(undefined);

interface BrandConfigProviderProps {
  children: ReactNode;
}

// Cache global para configuraciones de marca, compartido entre montajes (incluyendo StrictMode)
declare global {
  interface Window {
    PAGOS_BRAND_CACHE?: {
      configs: Record<string, MarcaConfig>;
      notFoundBrands: Set<string>;
    };
  }
}

const getGlobalBrandCache = () => {
  if (typeof window === 'undefined') {
    return {
      configs: {} as Record<string, MarcaConfig>,
      notFoundBrands: new Set<string>(),
    };
  }

  if (!window.PAGOS_BRAND_CACHE) {
    window.PAGOS_BRAND_CACHE = {
      configs: {} as Record<string, MarcaConfig>,
      notFoundBrands: new Set<string>(),
    };
  }

  return window.PAGOS_BRAND_CACHE;
};

// Cache en localStorage con TTL para persistir entre recargas
const BRAND_CACHE_PREFIX = 'BRAND_CONFIG_CACHE_';
const BRAND_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos (reducido de 1 hora para SWR)

interface CachedBrandConfig {
  config: MarcaConfig;
  timestamp: number;
}

const saveBrandToLocalCache = (keyword: string, config: MarcaConfig) => {
  try {
    const cached: CachedBrandConfig = {
      config,
      timestamp: Date.now()
    };
    localStorage.setItem(`${BRAND_CACHE_PREFIX}${keyword}`, JSON.stringify(cached));
  } catch {
    // Silent fail - localStorage might be full or unavailable
  }
};

const getBrandFromLocalCache = (keyword: string): { config: MarcaConfig; isExpired: boolean } | null => {
  try {
    const raw = localStorage.getItem(`${BRAND_CACHE_PREFIX}${keyword}`);
    if (!raw) return null;
    
    const cached: CachedBrandConfig = JSON.parse(raw);
    const age = Date.now() - cached.timestamp;
    const isExpired = age > BRAND_CACHE_TTL_MS;
    
    // Retornar config incluso si expiró (para SWR)
    return { config: cached.config, isExpired };
  } catch {
    return null;
  }
};

// Extraer el brandKeyword de la URL actual antes de inicializar el estado
const getInitialBrandKeyword = (): string => {
  if (typeof window === 'undefined') return 'acme';
  const segments = window.location.pathname.split('/').filter(Boolean);
  return segments.length > 0 ? segments[0] : 'acme';
};

// Campos críticos que requieren actualización inmediata de UI
const CRITICAL_FIELDS: (keyof MarcaConfig)[] = ['mantenimiento', 'portalPagos', 'googleTag'];

/**
 * Detecta si hay cambios críticos entre dos configuraciones
 * Cambios críticos: mantenimiento, portalPagos, googleTag
 */
const hasCriticalChanges = (oldConfig: MarcaConfig | null, newConfig: MarcaConfig): boolean => {
  if (!oldConfig) return false;
  
  for (const field of CRITICAL_FIELDS) {
    if (oldConfig[field] !== newConfig[field]) {
      return true;
    }
  }
  return false;
};

export const BrandConfigProvider: React.FC<BrandConfigProviderProps> = ({ children }) => {
  const location = useLocation();
  const [config, setConfig] = useState<MarcaConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(() => {
    const initialKeyword = getInitialBrandKeyword();
    const globalCache = getGlobalBrandCache();
    const hasConfig = !!globalCache.configs[initialKeyword];
    const is404 = globalCache.notFoundBrands.has(initialKeyword);
    return !hasConfig && !is404;
  });
  const [error, setError] = useState<string | null>(null);
  const [brandKeyword, setBrandKeyword] = useState<string>(getInitialBrandKeyword());
  const [isNotFound, setIsNotFound] = useState(false);
  
  // Refs para SWR y cancelación de fetches
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentKeywordRef = useRef<string>(getInitialBrandKeyword());

  /**
   * Revalida la configuración en background (SWR)
   */
  const revalidateInBackground = async (keyword: string, currentConfig: MarcaConfig) => {
    // Cancelar fetch anterior si existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    try {
      const freshConfig = await fetchBrandConfiguration(keyword);
      
      // Si el keyword cambió mientras hacíamos fetch, ignorar resultado
      if (currentKeywordRef.current !== keyword || controller.signal.aborted) {
        return;
      }
      
      // Actualizar cache global + localStorage
      const cache = getGlobalBrandCache();
      cache.configs[keyword] = freshConfig;
      saveBrandToLocalCache(keyword, freshConfig);
      // Solo actualizar UI si hay cambios críticos
      if (hasCriticalChanges(currentConfig, freshConfig)) {
        setConfig(freshConfig);
      }
    } catch {
      // Silenciar errores en background - el usuario ya tiene datos cacheados
    }
  };

  useEffect(() => {
    const loadBrandConfig = async () => {
      // Acceder al cache global directamente (estable, no dispara re-renders)
      const globalCache = getGlobalBrandCache();
      
      try {
        // Extraer el keyword de la URL usando location
        const segments = location.pathname.split('/').filter(Boolean);
        const keyword = segments.length > 0 ? segments[0] : 'acme';
        
        setBrandKeyword(keyword);
        currentKeywordRef.current = keyword;

        // Si es una ruta especial, no intentar cargar configuración de marca
        if (SPECIAL_ROUTES.includes(keyword)) {
          setIsNotFound(false);
          setLoading(false);
          return;
        }

        // PASO 1: Verificar cache en memoria global (más rápido)
        if (globalCache.configs[keyword]) {
          const cachedConfig = globalCache.configs[keyword];
          setConfig(cachedConfig);
          setIsNotFound(false);
          setLoading(false);
          
          // SWR: Revalidar en background
          revalidateInBackground(keyword, cachedConfig);
          return;
        }

        // PASO 2: Verificar cache en localStorage
        const localCached = getBrandFromLocalCache(keyword);
        if (localCached) {
          // Sincronizar con memoria global
          globalCache.configs[keyword] = localCached.config;
          setConfig(localCached.config);
          setIsNotFound(false);
          setLoading(false);
          
          // SWR: Revalidar en background
          revalidateInBackground(keyword, localCached.config);
          return;
        }

        // PASO 3: Verificar si ya sabemos que esta marca no existe (404 cache)
        if (globalCache.notFoundBrands.has(keyword)) {
          setConfig(null);
          setIsNotFound(true);
          setLoading(false);
          return;
        }

        // No está en cache, cargar desde el backend
        setLoading(true);
        setIsNotFound(false);

        // Llamar al servicio para obtener configuración
        const brandConfig = await fetchBrandConfiguration(keyword);
        
        // Si el keyword cambió mientras cargábamos, ignorar resultado
        if (currentKeywordRef.current !== keyword) {
          return;
        }
        
        // Actualizar window.PAGOS_CONFIG.marca con la nueva configuración
        if (typeof window !== 'undefined' && window.PAGOS_CONFIG) {
          window.PAGOS_CONFIG.marca = {
            ...window.PAGOS_CONFIG.marca,
            ...brandConfig
          };
        }
        
        // Guardar en cache global + localStorage
        globalCache.configs[keyword] = brandConfig;
        saveBrandToLocalCache(keyword, brandConfig);
        setConfig(brandConfig);
        setIsNotFound(false);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error loading brand config';
        console.error('[BrandConfigProvider] Error:', errorMessage);
        setError(errorMessage);
        
        // Detectar si es un error 404 (marca no encontrada)
        if (errorMessage.includes('404') || errorMessage.includes('not found')) {
          const segments = location.pathname.split('/').filter(Boolean);
          const keyword = segments.length > 0 ? segments[0] : 'acme';
          
          // Guardar en cache de 404s global
          globalCache.notFoundBrands.add(keyword);
          
          setIsNotFound(true);
          setConfig(null);
        }
      } finally {
        setLoading(false);
      }
    };

    loadBrandConfig();
    
    // Cleanup: cancelar fetch pendiente al desmontar o cambiar de ruta
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [location.pathname]); // Solo reacciona a cambios de ruta - cache global es estable

  return (
    <BrandConfigContext.Provider value={{ config, loading, error, brandKeyword, isNotFound }}>
      {children}
    </BrandConfigContext.Provider>
  );
};

/**
 * Hook para acceder al contexto de configuración de marca
 */
export const useBrandConfig = (): BrandConfigContextValue => {
  const context = useContext(BrandConfigContext);
  if (context === undefined) {
    throw new Error('useBrandConfig must be used within a BrandConfigProvider');
  }
  return context;
};
